import React, { useState, useEffect } from 'react';
import { Card, Button } from '../common';
import { theme } from '../../styles/theme';
import { supabase } from '../../supabaseClient';
import ChecklistForm from './ChecklistForm';
import ChecklistTable from './ChecklistTable';

const AuditChecklist = () => {
  const [view, setView] = useState(() => {
    const savedView = localStorage.getItem('auditChecklistView');
    return savedView || 'table';
  });
  const [selectedChecklist, setSelectedChecklist] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Save view state to localStorage
  useEffect(() => {
    localStorage.setItem('auditChecklistView', view);
  }, [view]);

  const handleCreateNew = () => {
    setSelectedChecklist(null);
    setView('form');
  };

  const handleEdit = async (checklist) => {
    setLoading(true);
    setError('');
    try {
      const { data, error: fetchError } = await supabase
        .from('audit_checklist')
        .select('*')
        .eq('id', checklist.id)
        .single();

      if (fetchError) throw fetchError;

      // Parse checklist_questions if it exists and is a string
      const parsedData = {
        ...data,
        checklist_questions: data.checklist_questions 
          ? (typeof data.checklist_questions === 'string' 
              ? JSON.parse(data.checklist_questions) 
              : data.checklist_questions)
          : [{ question: '', compliance_verified: false, evidence_observations: '' }]
      };

      setSelectedChecklist(parsedData);
      setView('form');
    } catch (err) {
      console.error('Error fetching checklist:', err);
      setError('Failed to load checklist');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSuccess = () => {
    setRefreshTrigger(prev => prev + 1);
    setView('table');
    setSelectedChecklist(null);
  };

  const handleCancel = () => {
    setView('table');
    setSelectedChecklist(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold" style={{ color: theme.colors.primary.main }}>
          Audit Checklists
        </h2>
        <Button
          onClick={() => view === 'table' ? handleCreateNew() : handleCancel()}
          variant="secondary"
        >
          {view === 'table' ? 'Create New Checklist' : 'View All Checklists'}
        </Button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-100 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {view === 'table' ? (
        <Card>
          <div className="p-6">
            <ChecklistTable 
              onEdit={handleEdit}
              refreshTrigger={refreshTrigger}
            />
          </div>
        </Card>
      ) : (
        <Card>
          <div className="p-6">
            <ChecklistForm
              checklist={selectedChecklist}
              onSaveSuccess={handleSaveSuccess}
              onCancel={handleCancel}
            />
          </div>
        </Card>
      )}
    </div>
  );
};

export default AuditChecklist;
